import { PageLayout } from "@/components/layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import PhotoUploader from "@/components/photo-uploader";
import { usePhotoValidation } from "@/hooks/usePhotoValidation";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/ui/info-card";
import { Link } from "wouter";
import { FileText, HelpCircle } from "lucide-react";
import { getCountryByCode, type DocType } from "@/lib/country-config";

export default function Validate() {
  const params = new URLSearchParams(window.location.search)
  const country = params.get('country') ?? undefined
  const rawDocType = params.get('docType')
  const docType: DocType | undefined =
    rawDocType === 'passport' || rawDocType === 'drivers_license' ? rawDocType : undefined
  const countryConfig = country ? getCountryByCode(country) : undefined
  const countryName = countryConfig?.name
  const documentLabel = docType === 'drivers_license' ? "driver's license" : "passport"
  const documentLabelTitle = docType === 'drivers_license' ? "Driver's License" : "Passport"
  const validatorTitle = countryName
    ? countryConfig?.requiresChoice
      ? `${countryName} ${documentLabelTitle} Photo Validator`
      : `${countryName} Photo Validator`
    : `${documentLabelTitle} Photo Validator`

  usePageMeta({
    title: `${validatorTitle} | Check And Order Online`,
    description: `Upload your ${documentLabel} photo, check it against the relevant requirements, crop it to the right size, and continue with a free digital download or print ordering.`,
    canonicalPath: "/validate",
  });

  const {
    selectedFile,
    validationResult,
    isValidating,
    handleFileSelect,
    handleRemoveFile,
    handleValidatePhoto,
    currentStep,
    handleReset,
  } = usePhotoValidation({ country, docType });

  return (
    <PageLayout>
      <section className="py-4 sm:py-6 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
          {validatorTitle}
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          Upload your photo, validate it against the relevant requirements, crop it to the required dimensions, and finish with a free digital download or printed order.
        </p>
        <p className="text-sm text-muted-foreground mt-4">
          Free digital download after validation. Printed photos are available to order.
        </p>
      </section>

      <section className="py-8 sm:py-12">
        <PhotoUploader
          documentLabel={documentLabel}
          selectedFile={selectedFile}
          onFileSelect={handleFileSelect}
          onRemoveFile={handleRemoveFile}
          onValidatePhoto={handleValidatePhoto}
          isValidating={isValidating}
          validationResult={validationResult}
          currentStep={currentStep}
          onReset={handleReset}
        />
      </section>

      <section className="py-8 sm:py-12">
        <InfoCard variant="info" className="text-center">
          <h3 className="text-lg font-semibold mb-2">Need Help Getting Started?</h3>
          <p className="mb-4">See the main rules we check and how passport and driver's license photo validation works from upload to ordering.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild>
              <Link href="/requirements">
                <FileText className="w-4 h-4" />
                View Photo Requirements
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link href="/how-it-works">
                <HelpCircle className="w-4 h-4" />
                How Validation Works
              </Link>
            </Button>
          </div>
        </InfoCard>
      </section>
    </PageLayout>
  );
}
